<script lang="ts">
	import { goto } from '$app/navigation';
	import { page } from '$app/stores';
	import { PUBLIC_API_BASE_URL } from '$env/static/public';
	import Footer from '$lib/components/Footer.svelte';
	import Header from '$lib/components/Header.svelte';
	import Preloader from '$lib/components/Preloader.svelte';
	import Button from '$lib/components/ui/Button.svelte';
	import Marquee from '$lib/components/ui/Marquee.svelte';
	import TechPopup from '$lib/components/ui/TechPopup.svelte';
	import ThankYouPopup from '$lib/components/ui/ThankYouPopup.svelte';
	import { currentLanguage, getLanguageFromPath, t } from '$lib/stores/i18n';
	import axios from 'axios';
	import { trackSelectContent } from '$lib/analytics/gtag';
	import { onDestroy, onMount } from 'svelte';

	export let promptText = ''; // 'text' or 'image'

	// Get current language from path or default to 'en'
	$: lang = getLanguageFromPath($page.url.pathname) || 'en';
	$: currentLanguage.set(lang);

	// 🖼️ Image slideshow state
	let currentImageIndex = 0;
	let slideInterval: ReturnType<typeof setInterval>;

	let isPopupOpen = false;
	let selectedTech: 'FDM' | 'SLA' | 'MJF' | 'SLM' = 'FDM';

	// 📧 Contact form state
	let isThankYouOpen = false;
	let isSubmitting = false;
	let contactFormData = {
		name: '',
		email: '',
		message: ''
	};

	const images = [
		'/assets/img/images/ff01.png',
		'/assets/img/images/ff02.png',
		'/assets/img/images/ff03.png'
	];

	// 📰 Marquee text items - will be reactive with translations
	$: marqueeItems = [$t('home.marquee.item1'), $t('home.marquee.item2')];

	onMount(() => {
		// 🔄 Auto slide images every 1.2 seconds
		slideInterval = setInterval(() => {
			currentImageIndex = (currentImageIndex + 1) % images.length;
		}, 1200);
	});

	onDestroy(() => {
		// 🧹 Cleanup interval
		if (slideInterval) {
			clearInterval(slideInterval);
		}
	});

	const handleClickGeneratePrompt = (e: MouseEvent) => {
		trackSelectContent({ content_type: 'cta_button', content_id: 'ai', content_group: 'ai_section', link_text: $t('home.aiButton') });
		console.log('Prompt type changed:', promptText);
		e.preventDefault();
		goto(`/${lang}/ai?prompttext=${encodeURIComponent(promptText)}`);
	};

	function openPopup(techType: 'FDM' | 'SLA' | 'MJF' | 'SLM') {
		selectedTech = techType;
		isPopupOpen = true;
	}
	function closePopup() {
		isPopupOpen = false;
	}

	// 📧 Contact form submission
	async function handleContactSubmit(event: Event) {
		event.preventDefault();

		if (isSubmitting) return;

		isSubmitting = true;

		const formData = new FormData();
		formData.append('name', contactFormData.name);
		formData.append('email', contactFormData.email);
		formData.append('message', contactFormData.message);

		const response = await axios.post(`${PUBLIC_API_BASE_URL}api/contact`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		});
		if (response.status === 200) {
			isThankYouOpen = true;
			contactFormData = { name: '', email: '', message: '' }; // Reset form
		} else {
			console.error('Contact form submission failed:', response.data);
		}
	}
</script>

<svelte:head>
	<title>{$t('meta.title')}</title>
	<meta name="description" content={$t('meta.description')} />
	<meta name="keywords" content="3d ai, 3dai, 3dai.co.th, {$t('meta.keywords')}" />
	<meta name="author" content="3DAI" />
	<meta name="robots" content="index, follow" />
	<meta property="og:type" content="website" />
	<meta property="og:title" content={$t('meta.title')} />
	<meta property="og:description" content={$t('meta.description')} />
	<meta property="og:url" content="https://3dai.co.th{$page.url.pathname || '/'}" />
	<meta property="og:site_name" content="3DAI" />
	<meta property="og:locale" content={lang === 'th' ? 'th_TH' : 'en_US'} />

	<!-- FAQPage JSON-LD is in app.html (static) so crawlers get valid JSON -->
</svelte:head>

<!--Preloader-->
<Preloader />

<!--Preloader-end -->

<!-- Scroll-top -->
<button aria-label="Search" class="scroll__top scroll-to-target" data-target="html">
	<i class="fas fa-chevron-up"></i>
</button>
<!-- Scroll-top-end-->

<Header />

<!-- main-area -->
<main class="main-area fix">
	<!-- area-bg -->
	<div class="area__bg" style="background-image: url('/assets/img/bg/area_bg.jpg');">
		<!-- banner-area -->
		<section class="banner__area">
			<div class="container">
				<div class="row align-items-center">
					<div class="col-lg-4 order-0 order-lg-2">
						<div class="banner__img alltuchtopdown text-center">
							<!-- 🖼️ Svelte reactive image slideshow -->
							{#each images as image, index}
								<img
									src={image}
									alt="img"
									class=" slideshow-image {index === currentImageIndex ? 'active' : ''}"
								/>
							{/each}
						</div>
					</div>
					<div class="col-lg-8">
						<div class="banner__content">
							<span class="sub-title">
								<svg
									width="8"
									height="16"
									viewBox="0 0 8 16"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M2 0H0V1V15V16H2V13.25L7.13991 8.75258C7.59524 8.35417 7.59524 7.64584 7.13991 7.24742L2 2.75V0Z"
										fill="currentColor"
									/>
								</svg>
								{$t('home.subtitle')}
							</span>
							<h2 class="title">
								{$t('home.title')} <span>{$t('home.titleHighlight')}</span>
								{$t('home.titleEnd')}
							</h2>
							<div class="banner__btn">
								<Button href="/{lang}/ai" className="tg-btn tg-btn-one"
									onClick={() => trackSelectContent({ content_type: 'cta_button', content_id: 'ai', content_group: 'hero', link_text: $t('home.aiButton') })}
									>{$t('home.aiButton')}</Button
								>
								<Button href="/{lang}/3dprintingservice" className="tg-btn tg-btn-two"
									onClick={() => trackSelectContent({ content_type: 'cta_button', content_id: '3dprintingservice', content_group: 'hero', link_text: $t('home.serviceButton') })}
									>{$t('home.serviceButton')}</Button
								>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
		<!-- banner-area-end -->

		<!-- GEO: clear summary for generative engines (crawlable, quotable) -->
		<section class="section-py-120 geo-section" id="about-3dai" aria-labelledby="about-3dai-heading">
			<div class="container">
				<div class="row justify-content-center">
					<div class="col-lg-10 col-xl-9">
						<div class="geo-card">
							<h2 id="about-3dai-heading" class="geo-heading">{$t('home.geo.heading')}</h2>
							<span class="geo-heading-accent" aria-hidden="true"></span>
							<p class="geo-summary">
								{$t('home.geo.summary')}
							</p>
							<h3 class="geo-workflow-title">{$t('home.geo.workflowTitle')}</h3>
							<ul class="geo-workflow">
								<li class="geo-workflow-item">
									<span class="geo-step-icon" aria-hidden="true"><i class="fas fa-keyboard"></i></span>
									<span class="geo-step-text">{$t('home.geo.step1')}</span>
								</li>
								<li class="geo-workflow-item">
									<span class="geo-step-icon" aria-hidden="true"><i class="fas fa-magic"></i></span>
									<span class="geo-step-text">{$t('home.geo.step2')}</span>
								</li>
								<li class="geo-workflow-item">
									<span class="geo-step-icon" aria-hidden="true"><i class="fas fa-eye"></i></span>
									<span class="geo-step-text">{$t('home.geo.step3')}</span>
								</li>
								<li class="geo-workflow-item">
									<span class="geo-step-icon" aria-hidden="true"><i class="fas fa-download"></i></span>
									<span class="geo-step-text">{$t('home.geo.step4')}</span>
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- AI-area -->
		<section class="AI__area">
			<div class="container">
				<div class="row align-items-center">
					<div class="col-lg-6 order-0 order-lg-1">
						<span class="sub-title">
							<svg
								width="8"
								height="16"
								viewBox="0 0 8 16"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									fill-rule="evenodd"
									clip-rule="evenodd"
									d="M2 0H0V1V15V16H2V13.25L7.13991 8.75258C7.59524 8.35417 7.59524 7.64584 7.13991 7.24742L2 2.75V0Z"
									fill="currentColor"
								/>
							</svg>
							{$t('home.aiSection.subtitle')}
						</span>
						<h2 class="title">
							{$t('home.aiSection.title')} <span>{$t('home.aiSection.titleHighlight')}</span>
							{lang === 'th' ? $t('home.aiSection.titleEnd') : ''}
						</h2>
						<div class="banner__img text-center">
							<img src="/assets/img/images/choose-icon01.png" alt="img" class="alltuchtopdown" />
						</div>
					</div>
					<div class="col-lg-6 order-1 order-lg-2">
						<div>
							<h6>
								{$t('home.aiSection.description')}
							</h6>
							<div class="prompt__input">
								<input
									class="prompt_text"
									type="text"
									placeholder={$t('home.aiSection.placeholder')}
									name="prompt_text"
									bind:value={promptText}
								/>
								<Button onClick={(e) => handleClickGeneratePrompt(e)} style="width: 100%;"
									>{$t('home.aiButton')}</Button
								>
							</div>
							<p>
								{$t('home.aiSection.tip')}
							</p>
						</div>
					</div>
				</div>
			</div>
		</section>
		<!-- AI-area-end -->
	</div>
	<!-- area-bg-end -->

	<!-- roadMap-area -->
	<section id="roadmap" class="roadMap__area">
		<div class="container">
			<div class="row">
				<div class="col-lg-4">
					<div class="roadMap__content-wrap">
						<div class="section__title mb-50">
							<span class="sub-title">
								<svg
									width="8"
									height="16"
									viewBox="0 0 8 16"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M2 0H0V1V15V16H2V13.25L7.13991 8.75258C7.59524 8.35417 7.59524 7.64584 7.13991 7.24742L2 2.75V0Z"
										fill="currentColor"
									/>
								</svg>
								{$t('home.roadmap.subtitle')}
							</span>
							<h2 class="title">{$t('home.roadmap.title')}</h2>
						</div>
						<div class="roadMap__img">
							<img src="/assets/img/images/roadmap_img.png" alt="img" class="alltuchtopdown" />
						</div>
						<div class="roadMap__shape">
							<img src="/assets/img/images/roadmap_shape.png" alt="shape" />
						</div>
						<div class="center">
							<Button href="#contact" className="tg-btn tg-btn-one"
								onClick={() => trackSelectContent({ content_type: 'cta_button', content_id: 'contact', content_group: 'roadmap', link_text: $t('common.getStarted') })}
								>{$t('common.getStarted')}</Button
							>
						</div>
					</div>
				</div>
				<div class="col-lg-8">
					<div class="roadMap__item-wrap">
						<div class="roadMap__line">
							<span class="progress-line"></span>
						</div>
						<div class="roadMap__item">
							<h4 class="title"><strong> {$t('home.roadmap.step1.title')}</strong></h4>
							<p>{$t('home.roadmap.step1.description')}</p>
						</div>
						<div class="roadMap__item">
							<h4 class="title"><strong> {$t('home.roadmap.step2.title')}</strong></h4>
							<p>
								{$t('home.roadmap.step2.description')}
							</p>
						</div>

						<div class="roadMap__item">
							<h4 class="title"><strong> {$t('home.roadmap.step3.title')}</strong></h4>
							<p>
								{$t('home.roadmap.step3.description')}
							</p>
						</div>
						<div class="roadMap__item">
							<h4 class="title"><strong> {$t('home.roadmap.step4.title')}</strong></h4>
							<p>
								{$t('home.roadmap.step4.description')}
							</p>
						</div>

						<div class="roadMap__item">
							<h4 class="title"><strong> {$t('home.roadmap.step5.title')}</strong></h4>
							<p>
								{$t('home.roadmap.step5.description')}
							</p>
						</div>
						<div class="roadMap__item">
							<h4 class="title"><strong> {$t('home.roadmap.step6.title')}</strong></h4>
							<p>
								{$t('home.roadmap.step6.description')}
							</p>
						</div>
						<div class="roadMap__item">
							<h4 class="title"><strong> {$t('home.roadmap.step7.title')}</strong></h4>
							<p>
								{$t('home.roadmap.step7.description')}
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
	<!-- roadMap-area-end -->

	<!-- marquee-area -->
	<section class="marquee__area">
		<Marquee items={marqueeItems} speed={50} direction="left" gap={9} pauseOnHover={true} />
	</section>
	<!-- marquee-area-end -->

	<!-- area-bg -->
	<div class="area__bg-three" style="background-image: url('/assets/img/bg/area_bg_03.jpg');">
		<!-- summary-area -->
		<section class="summary__area section-py-120">
			<div id="particles-js">
				<canvas
					class="particles-js-canvas-el"
					width="660"
					height="896"
					style="width: 100%; height: 100%;"
				></canvas>
			</div>
			<script>
				particlesJS('particles-js', {
					particles: {
						number: {
							value: 120,
							density: {
								enable: true,
								value_area: 800
							}
						},
						color: {
							value: '#ffffff'
						},
						shape: {
							type: 'circle',
							stroke: {
								width: 0,
								color: '#000000'
							},
							polygon: {
								nb_sides: 5
							},
							image: {
								src: 'img/github.svg',
								width: 100,
								height: 100
							}
						},
						opacity: {
							value: 0.5,
							random: false,
							anim: {
								enable: false,
								speed: 1,
								opacity_min: 0.1,
								sync: false
							}
						},
						size: {
							value: 4,
							random: true,
							anim: {
								enable: false,
								speed: 40,
								size_min: 0.1,
								sync: false
							}
						},
						line_linked: {
							enable: true,
							distance: 150,
							color: '#ffffff',
							opacity: 0.4,
							width: 1
						},
						move: {
							enable: true,
							speed: 6,
							direction: 'none',
							random: false,
							straight: false,
							out_mode: 'out',
							bounce: false,
							attract: {
								enable: false,
								rotateX: 600,
								rotateY: 1200
							}
						}
					},
					interactivity: {
						detect_on: 'canvas',
						events: {
							onhover: {
								enable: true,
								mode: 'grab'
							},
							onclick: {
								enable: true,
								mode: 'push'
							},
							resize: true
						},
						modes: {
							grab: {
								distance: 100,
								line_linked: {
									opacity: 1
								}
							},
							bubble: {
								distance: 400,
								size: 40,
								duration: 2,
								opacity: 8,
								speed: 3
							},
							repulse: {
								distance: 200,
								duration: 0.4
							},
							push: {
								particles_nb: 4
							},
							remove: {
								particles_nb: 2
							}
						}
					},
					retina_detect: true
				});
			</script>
			<div class="container">
				<div class="row justify-content-center">
					<div class="col-lg-6">
						<div class="section__title text-center mb-80">
							<span class="sub-title"
								><svg
									width="8"
									height="16"
									viewBox="0 0 8 16"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									><path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M2 0H0V1V15V16H2V13.25L7.13991 8.75258C7.59524 8.35417 7.59524 7.64584 7.13991 7.24742L2 2.75V0Z"
										fill="currentColor"
									></path></svg
								>
								{$t('home.technology.subtitle')}</span
							>
							<h2 class="title">{$t('home.technology.title')}</h2>
						</div>
					</div>
				</div>
				<div class="row justify-content-center gutter-y-30">
					<div class="col-lg-3 col-md-6">
						<button class="choose__item text-center" on:click={() => openPopup('FDM')}>
							<div class="choose__icon">
								<img src="/assets/img/icons/fdm.png" alt="icon" />
							</div>
							<div class="choose__content">
								<h4 class="title">FDM</h4>
								<p>Fused Deposition Modeling.</p>
							</div>
						</button>
					</div>
					<div class="col-lg-3 col-md-6">
						<button class="choose__item text-center" on:click={() => openPopup('SLA')}>
							<div class="choose__icon">
								<img src="/assets/img/icons/sla.png" alt="icon" />
							</div>
							<div class="choose__content">
								<h4 class="title">SLA</h4>
								<p>Stereolithography</p>
							</div>
						</button>
					</div>
					<div class="col-lg-3 col-md-6">
						<button class="choose__item text-center" on:click={() => openPopup('MJF')}>
							<div class="choose__icon">
								<img src="/assets/img/icons/mjf.png" alt="icon" />
							</div>
							<div class="choose__content">
								<h4 class="title">MJF</h4>
								<p>Multi Jet Fusion.</p>
							</div>
						</button>
					</div>
					<div class="col-lg-3 col-md-6">
						<button class="choose__item text-center" on:click={() => openPopup('SLM')}>
							<div class="choose__icon">
								<img src="/assets/img/icons/slm.png" alt="icon" />
							</div>
							<div class="choose__content">
								<h4 class="title">SLM</h4>
								<p>Selective Laser Melting.</p>
							</div>
						</button>
					</div>
				</div>
				<div class="col-12" style="display: flex; justify-content: center;margin-top: 40px;">
					<Button
						href="/{lang}/3dprintingservice"
						className="tg-btn tg-btn-one"
						style="margin-top: 40px; width: 20%;"
						onClick={() => trackSelectContent({ content_type: 'cta_button', content_id: '3dprintingservice', content_group: 'technology', link_text: $t('home.technology.getQuote') })}
						>{$t('home.technology.getQuote')}</Button
					>
				</div>
			</div>
			<TechPopup bind:isOpen={isPopupOpen} techType={selectedTech} on:close={closePopup} />
		</section>
		<!-- summary-area-end -->
	</div>
	<!-- area-bg-end -->

	<!-- FAQ section (AEO: clear Q&A for answer engines) -->
	<section class="section-py-80" id="faq-3dai" aria-labelledby="faq-heading">
		<div class="container">
			<div class="row justify-content-center">
				<div class="col-lg-8">

						<h3>{$t('home.faq.title')}</h3>
					<dl class="faq-list">
						<div class="faq-item">
							<dt><strong>{$t('home.faq.q1')}</strong></dt>
							<dd>{$t('home.faq.a1')}</dd>
						</div>
						<div class="faq-item">
							<dt><strong>{$t('home.faq.q2')}</strong></dt>
							<dd>{$t('home.faq.a2')}</dd>
						</div>
						<div class="faq-item">
							<dt><strong>{$t('home.faq.q3')}</strong></dt>
							<dd>{$t('home.faq.a3')}</dd>
						</div>
					</dl>
				</div>
			</div>
		</div>
	</section>

	<!-- faq-area -->
	<section id="faq" class="faq__area section-py-120">
		<div class="container">
			<div class="row">
				<div class="col-lg-6">
					<div class="faq__content">
						<div class="section__title mb-20">
							<span class="sub-title">
								<svg
									width="8"
									height="16"
									viewBox="0 0 8 16"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M2 0H0V1V15V16H2V13.25L7.13991 8.75258C7.59524 8.35417 7.59524 7.64584 7.13991 7.24742L2 2.75V0Z"
										fill="currentColor"
									/>
								</svg>
								{$t('home.contact.subtitle')}
							</span>
							<h2 class="title">{$t('home.contact.title')}</h2>
						</div>
						<div class="progress__wrap">
							<div class="progress__item">
								<div class="progress__item-top">
									<h3 class="progress__title">
										{$t('home.contact.officeHours')}
										<span>: {$t('home.contact.officeHoursValue')}</span>
									</h3>
								</div>
								<div class="progress">
									<div class="progress-bar" style="width: 56%;"></div>
								</div>
							</div>
							<div class="progress__item">
								<div class="progress__item-top">
									<h3 class="progress__title">
										{$t('home.contact.email')}<span>: {$t('home.contact.emailValue')}</span>
									</h3>
								</div>
								<div class="progress">
									<div class="progress-bar" style="width: 32%;"></div>
								</div>
							</div>
							<div class="progress__item">
								<div class="progress__item-top">
									<h3 class="progress__title">
										{$t('home.contact.hotline')} <span>: {$t('home.contact.hotlineValue')} </span>
									</h3>
								</div>
								<div class="progress">
									<div class="progress-bar" style="width: 29%;"></div>
								</div>
							</div>
						</div>
						<ul class="download__icon-wrap list-wrap">
							<li>
								<a
									href="https://www.facebook.com/3dadditiveindustry"
									aria-label="facebook"
									target="_blank"
									class="download__icon"
								>
									<div class="icon-shape">
										<img src="/assets/img/icons/download_icon_shape.svg" alt="icon" />
									</div>
									<img src="/assets/img/icons/contact_icon01.png" alt="icon" class="icon" />
								</a>
							</li>
							<li>
								<a href="https://line.me/R/ti/p/@833yovnz" aria-label="line" class="download__icon">
									<div class="icon-shape">
										<img src="/assets/img/icons/download_icon_shape.svg" alt="icon" />
									</div>
									<img src="/assets/img/icons/contact_icon02.png" alt="icon" class="icon" />
								</a>
							</li>
							<li>
								<a href="mailto:sales@3dai.co.th" aria-label="email" class="download__icon">
									<div class="icon-shape">
										<img src="/assets/img/icons/download_icon_shape.svg" alt="icon" />
									</div>
									<img src="/assets/img/icons/contact_icon03.png" alt="icon" class="icon" />
								</a>
							</li>
							<li>
								<a href="tel: 091-428-2956" aria-label="phone" class="download__icon">
									<div class="icon-shape">
										<img src="/assets/img/icons/download_icon_shape.svg" alt="icon" />
									</div>
									<img src="/assets/img/icons/contact_icon04.png" alt="icon" class="icon" />
								</a>
							</li>
						</ul>
					</div>
				</div>
				<div class="col-lg-6">
					<div class="address">
						<div class="address__icon">
							<img src="/assets/img/icons/contact_icon05.png" alt="icon" class="icon" />
							<h5 class="title">{$t('home.contact.address')}</h5>
						</div>
						<p>
							{$t('home.contact.addressValue')}
						</p>
					</div>
					<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
					<iframe
						title="Google Map showing 3D Printer Shop location"
						src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3876.9146543219904!2d100.6943295!3d13.6629533!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30e2a1c891b93b9b%3A0x523fa6bbe5b431e!2zM0QgQWRkaXRpdmUgSW5kdXN0cnkgQ28uLEx0ZCAtIDNEIFByaW50ZXIg4LmA4LiE4Lij4Li34LmI4Lit4LiH4Lie4Li04Lih4Lie4LmMIDMg4Lih4Li04LiV4Li0IC0gM0QgU2Nhbm5lciDguYDguITguKPguLfguYjguK3guIfguKrguYHguIHguJkgMyDguKHguLTguJXguLQ!5e0!3m2!1sth!2sth!4v1659129537157!5m2!1sth!2sth"
						width="660"
						height="375"
						frameborder="0"
						style="border:solid 1px;border-radius:5px;"
						aria-hidden="false"
						tabindex="0"
						allowfullscreen
						loading="lazy"
					></iframe>
				</div>
			</div>
		</div>
	</section>
	<!-- faq-area-end -->

	<!-- contact-area -->
	<section id="contact" class="contact__area" style="position: relative;z-index: 0;">
		<div class="container">
			<div class="contact__inner-wrap">
				<div class="row justify-content-center">
					<div class="col-lg-8">
						<div class="section__title text-center mb-50">
							<span class="sub-title white-title">
								<svg
									width="8"
									height="16"
									viewBox="0 0 8 16"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M2 0H0V1V15V16H2V13.25L7.13991 8.75258C7.59524 8.35417 7.59524 7.64584 7.13991 7.24742L2 2.75V0Z"
										fill="currentColor"
									/>
								</svg>
								{$t('home.contact.form.subtitle')}
							</span>
							<h2 class="title">{$t('home.contact.form.title')}</h2>
						</div>
					</div>
					<div class="col-xl-6 col-lg-8">
						<form on:submit={handleContactSubmit} class="contact__form">
							<div class="row">
								<div class="col-lg-6">
									<div class="form-grp">
										<label for="name">{$t('home.contact.form.name')}</label>
										<input
											id="name"
											name="name"
											type="text"
											placeholder={$t('home.contact.form.namePlaceholder')}
											bind:value={contactFormData.name}
											required
											disabled={isSubmitting}
										/>
									</div>
								</div>
								<div class="col-lg-6">
									<div class="form-grp">
										<label for="email">{$t('home.contact.form.email')}</label>
										<input
											id="email"
											name="email"
											type="email"
											placeholder={$t('home.contact.form.emailPlaceholder')}
											bind:value={contactFormData.email}
											required
											disabled={isSubmitting}
										/>
									</div>
								</div>
							</div>
							<div class="form-grp">
								<label for="message">{$t('home.contact.form.message')}</label>
								<textarea
									name="message"
									id="message"
									placeholder={$t('home.contact.form.messagePlaceholder')}
									bind:value={contactFormData.message}
									required
									disabled={isSubmitting}
								></textarea>
							</div>
							<div class="submit__btn text-center">
								<button type="submit" class="tg-btn tg-btn-two" disabled={isSubmitting}>
									<img src="/assets/img/icons/btn_right_arrow.svg" alt="icon" class="right_arrow" />
									<span>
										<img src="/assets/img/icons/btn_icon_03.svg" alt="" />
										{isSubmitting ? $t('common.submitting') : $t('home.contact.form.submit')}
									</span>
								</button>
							</div>
						</form>
						<p class="ajax-response mb-0 text-center"></p>
					</div>
				</div>
				<div class="contact__shape-wrap">
					<img src="/assets/img/images/contact_shape01.png" alt="img" class="alltuchtopdown" />
					<img src="/assets/img/images/contact_shape02.png" alt="img" />
				</div>
			</div>
		</div>
	</section>
	<!-- contact-area-end -->
</main>
<!-- main-area-end -->

<!-- Thank You Popup -->
<ThankYouPopup
	bind:isOpen={isThankYouOpen}
	title={$t('home.thankYou.title')}
	message={$t('home.thankYou.message')}
	on:close={() => (isThankYouOpen = false)}
/>

<Footer />

<style>
	.area__bg {
		background-size: cover;
		background-position: center;
		background-repeat: no-repeat;
		background-attachment: scroll;
	}

	.area__bg-three {
		background-size: cover;
		background-position: center;
		background-repeat: no-repeat;
		background-attachment: scroll;
	}

	/* 🎬 Smooth image transition */
	.banner__img {
		transition: opacity 0.5s ease-in-out;
	}
	.slideshow-image {
		display: none;
	}

	.slideshow-image:hover {
		transform: scale(1.05);
		/* transition: transform 0.3s ease; */
	}
	.slideshow-image.active {
		display: block;
	}
	/* 📰 Marquee section styling - แบบเดิม ไม่มีพื้นหลัง */
	.marquee__area {
		background: transparent;
		padding: 60px 0;
		position: relative;
		overflow: hidden;
		z-index: 0;
	}

	.marquee__area :global(.marquee-container) {
		position: relative;
		z-index: 2;
	}

	.choose__content {
		z-index: 999;
		position: relative;
	}

	.faq-list { list-style: none; margin: 0; padding: 0; }
	.faq-item { margin-bottom: 1.25rem; }
	.faq-item dt { margin-bottom: 0.35rem; font-size: 1rem; }
	.faq-item dd { margin: 0; color: var(--text-muted, #aaa); line-height: 1.5; }

	/* GEO section: readability + beauty on dark background */
	.geo-section {
		position: relative;
	}
	.geo-card {
		position: relative;
		padding: 2.5rem 2rem 2.5rem 2.5rem;
		background: linear-gradient(135deg, rgba(24, 24, 28, 0.85) 0%, rgba(18, 18, 22, 0.92) 100%);
		border: 1px solid rgba(255, 255, 255, 0.06);
		border-left: 4px solid #c41e3a;
		border-radius: 12px;
		box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
	}
	.geo-card .geo-heading {
		color: #fff;
		font-size: 1.5rem;
		font-weight: 700;
		margin-bottom: 0.5rem;
		letter-spacing: -0.02em;
	}
	.geo-heading-accent {
		display: block;
		width: 48px;
		height: 3px;
		background: linear-gradient(90deg, #c41e3a, rgba(196, 30, 58, 0.5));
		border-radius: 2px;
		margin-bottom: 1.5rem;
	}
	.geo-section .geo-summary {
		color: #e2e2e2;
		font-size: 1.05rem;
		line-height: 1.75;
		margin-bottom: 2rem;
	}
	.geo-section .geo-workflow-title {
		color: #c9c9c9;
		letter-spacing: 0.08em;
		font-weight: 600;
		font-size: 0.8rem;
		text-transform: uppercase;
		margin-bottom: 1.25rem;
	}
	.geo-workflow {
		list-style: none;
		padding: 0;
		margin: 0;
		display: grid;
		gap: 1rem;
	}
	.geo-workflow-item {
		display: flex;
		align-items: flex-start;
		gap: 1rem;
		color: #d8d8d8;
		font-size: 1rem;
		line-height: 1.6;
	}
	.geo-step-icon {
		flex-shrink: 0;
		width: 40px;
		height: 40px;
		border-radius: 10px;
		background: rgba(196, 30, 58, 0.2);
		color: #e85c7a;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 0.95rem;
	}
	.geo-step-text {
		flex: 1;
		padding-top: 0.25rem;
	}
	@media (max-width: 767.98px) {
		.geo-card {
			padding: 1.75rem 1.25rem;
			border-radius: 10px;
		}
		.geo-card .geo-heading { font-size: 1.3rem; }
		.geo-step-icon { width: 36px; height: 36px; font-size: 0.85rem; }
	}
</style>
